import { APP_NAMES, type AppType } from '@common/core-layer/stores/global.store'

import type { ProfileMenuIconName } from '../types/profile-menu'

export const PROFILE_MENU_OPTIONS = {
  PROFILE_SETTINGS: 'profile-settings',
  ACCOUNT_SETTINGS: 'account-settings',
  MANAGE_PURCHASES: 'manage-purchases',
  INTEGRATIONS: 'integrations',
  KOLLAB_STUDIO: 'kollab-studio',
  MANAGE_PAYMENTS: 'manage-payments',
  AFFILIATES: 'affiliates',
  LOGOUT: 'logout',
} as const

export type ProfileMenuOption =
  | 'profile-settings'
  | 'account-settings'
  | 'manage-purchases'
  | 'integrations'
  | 'kollab-studio'
  | 'manage-payments'
  | 'affiliates'
  | 'logout'

/** Internal: use CPNavigateTo with route name. External: full navigation (path or absolute URL). */
export type ProfileMenuNavigation =
  | { type: 'internal'; name: string; query?: Record<string, string>; newTab?: boolean }
  | { type: 'external'; path: string; newTab?: boolean }

/** Revex ProfileCardV2 visibility gates (clientportal-core GoKollabTopBar). */
export interface ProfileMenuVisibility {
  /**
   * Hide on compact (mobile/tablet) viewports — revex parity gates these on
   * `AppState.isMobile` (<= 1024px), so tablets are treated as mobile.
   * Applies to manage purchases, studio, integrations, affiliates.
   */
  hideOnMobile?: boolean
  /** Only when user has a provisioned GoKollab user-location (manage payments). */
  requiresUserLocation?: boolean
}

/**
 * Profile menu item configuration.
 * `showOnApps` restricts the item to specific apps. Include all `APP_NAMES` values to show everywhere.
 */
export const PROFILE_MENU_ITEMS: ReadonlyArray<{
  key: (typeof PROFILE_MENU_OPTIONS)[keyof typeof PROFILE_MENU_OPTIONS]
  labelKey: string
  icon: ProfileMenuIconName
  navigation: ProfileMenuNavigation
  showOnApps: ReadonlyArray<AppType>
  visibility?: ProfileMenuVisibility
}> = [
  {
    key: PROFILE_MENU_OPTIONS.PROFILE_SETTINGS,
    labelKey: 'profileMenu.profileSettings',
    icon: 'UserCircleIcon',
    navigation: { type: 'internal', name: 'account-settings', query: { activeTab: 'profile' } },
    showOnApps: [APP_NAMES.CLIENTPORTAL, APP_NAMES.GOKOLLAB, APP_NAMES.COURSES, APP_NAMES.MEET],
  },
  {
    key: PROFILE_MENU_OPTIONS.ACCOUNT_SETTINGS,
    labelKey: 'profileMenu.accountSettings',
    icon: 'Settings01Icon',
    navigation: { type: 'internal', name: 'account-settings', query: { activeTab: 'account' } },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
  },
  {
    key: PROFILE_MENU_OPTIONS.MANAGE_PURCHASES,
    labelKey: 'profileMenu.managePurchases',
    icon: 'ShoppingBag02Icon',
    navigation: { type: 'external', path: '/manage/purchases/communities' },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
    visibility: { hideOnMobile: true },
  },
  {
    key: PROFILE_MENU_OPTIONS.INTEGRATIONS,
    labelKey: 'profileMenu.integrations',
    icon: 'Dataflow01Icon',
    navigation: { type: 'external', path: '/integrations' },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
    visibility: { hideOnMobile: true, requiresUserLocation: true },
  },
  {
    key: PROFILE_MENU_OPTIONS.KOLLAB_STUDIO,
    labelKey: 'profileMenu.kollabStudio',
    icon: 'Star05Icon',
    navigation: { type: 'external', path: '/studio' },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
    visibility: { hideOnMobile: true },
  },
  {
    key: PROFILE_MENU_OPTIONS.MANAGE_PAYMENTS,
    labelKey: 'profileMenu.managePayments',
    icon: 'CreditCard01Icon',
    navigation: {
      type: 'external',
      path: '/payments-integration',
    },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
    visibility: { requiresUserLocation: true },
  },
  {
    key: PROFILE_MENU_OPTIONS.AFFILIATES,
    labelKey: 'profileMenu.affiliates',
    icon: 'Users01Icon',
    navigation: { type: 'external', path: '/affiliate-manager' },
    showOnApps: [APP_NAMES.GOKOLLAB, APP_NAMES.COURSES],
    visibility: { hideOnMobile: true },
  },
]
